// Reusable modal with dialog semantics, focus trap, body scroll lock, dark mode, and configurable width.
// Uses the soft-lg overlay-tier shadow so the dialog separates from any card elevation beneath it.

import { useId, useRef } from 'react';
import { X } from 'lucide-react';
import Button from './Button';
import { useFocusTrap } from '../../hooks/useFocusTrap';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  titleAccessory?: React.ReactNode;
  children: React.ReactNode;
  maxWidth?: string; // default: 'sm:max-w-lg'
}

export default function Modal({ isOpen, onClose, title, titleAccessory, children, maxWidth = 'sm:max-w-lg' }: ModalProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  // enabled: isOpen re-arms the trap on the closed → open transition. The
  // modal is eagerly mounted while closed (panelRef is null then), so an
  // always-enabled trap would fire once at mount, bail on the null ref, and
  // never re-run — leaving Escape/scroll-lock/auto-focus dead for its life.
  useFocusTrap(panelRef, { onEscape: onClose, enabled: isOpen });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div
          className="fixed inset-0 bg-slate-900/50 transition-opacity"
          onClick={onClose}
        />
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className={`relative transform overflow-hidden rounded-xl bg-white text-left shadow-soft-lg dark:shadow-soft-lg-dark transition-all animate-scale-in sm:my-8 sm:w-full ${maxWidth} dark:bg-slate-800 dark:border dark:border-slate-700`}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-1.5">
              <h3 id={titleId} className="text-h3 text-slate-900 dark:text-slate-100">{title}</h3>
              {titleAccessory}
            </div>
            <Button variant="ghost" iconOnly size="sm" icon={<X className="h-5 w-5" />} aria-label="Close" onClick={onClose} />
          </div>
          <div className="px-6 py-4">{children}</div>
        </div>
      </div>
    </div>
  );
}
